/**
 * VibeLive API — Thin facade over VibeLiveCore
 * 
 * Attaches a clean `window.VibeLive` object so HTML onclick handlers
 * work without module-scope workarounds.
 * 
 * Setup (no import map needed):
 * 
 *   <script type="module">
 *     import VibeLive from 'https://makedo.com/<devctx>/sdk/vibelive-api.js';
 *     VibeLive.init({ contextId: 'your-context-id' });           // trial project
 *     VibeLive.init({ contextId: 'xxx', proxy: '/my-proxy' });   // production project
 *     VibeLive.on('remoteStreamStart', (memberId, streamType) => { ... });
 *   </script>
 * 
 *   <button onclick="VibeLive.signup('Alex')">Join</button>
 *   <button onclick="VibeLive.enterByRoomCode('X7kQ3m')">Enter Room</button>
 *   <button onclick="VibeLive.startLive()">Go Live</button>
 */

import MakedoConfig from './_config.js';
import VibeLiveCore from './vibelive-core.js';
import { MakedoError, ErrorCodes } from './errors.js';

let chat = null;

function requireInit() {
    if (!chat) throw new MakedoError(ErrorCodes.INIT_REQUIRED, 'VibeLive.init() has not been called. Call VibeLive.init({ projectId: "..." }) before using any methods.');
}

/**
 * Create an independent VibeLive instance with its own state, events, and connection.
 * For multi-room scenarios (e.g. observing 3 meetings simultaneously).
 * Each instance gets its own VibeLiveCore, Bridge, and WebRTC connection.
 *
 * @param {Object} config - Same config as VibeLive.init()
 * @returns {Object} A standalone VibeLive-like object with all the same methods/getters
 */
function createInstance(config = {}) {
    MakedoConfig.configure(config.serverUrl);
    const core = new VibeLiveCore(config);

    function req() {
        if (!core) throw new MakedoError(ErrorCodes.INIT_REQUIRED, 'This VibeLive instance was not initialized.');
    }

    const instance = {
        async signup(name) { req(); return core.signupAnonymous(name); },
        async login(email, password, contextId) { req(); return core.login(email, password, contextId); },
        async restoreLogin() { req(); return core.restoreLogin(); },
        async logout() { req(); return core.logout(); },
        async createRoom(title, options = {}) { req(); return core.createChannel({ title, ...options }); },
        async enterByRoomCode(roomCode, displayName = null) { req(); return core.enterByRoomCode(roomCode, displayName); },
        exitRoom() { req(); core.exitChannel(); },
        async loadChannels() { req(); return core.loadChannels(); },
        async selectChannel(channelId, displayName = null) { req(); return core.selectChannel(channelId, displayName); },
        async loadUsers() { req(); return core.loadUsers(); },
        async selectUser(userId) { req(); return core.selectUser(userId); },
        async getChannelInfo(identifier) { req(); return core.getChannelInfo(identifier); },
        async updateRoom(options, channelId) { req(); return core.updateRoom(options, channelId); },
        async setRoomMetadata(metadata, channelId) { req(); return core.setRoomMetadata(metadata, channelId); },
        async kickMember(memberId, message) { req(); return core.kickMember(memberId, message); },
        backToList() { req(); core.backToChannels(); },
        async startLive() { req(); return core.startLive(); },
        stopLive(keepMedia = true) { req(); core.stopLive(keepMedia); },
        async setVideo(on) { req(); return core.setVideo(on); },
        async setAudio(on) { req(); return core.setAudio(on); },
        async setScreenshare(on) { req(); return core.setScreenshare(on); },
        async setCamera(deviceId) { req(); return core.setCamera(deviceId); },
        async setMicrophone(deviceId) { req(); return core.setMicrophone(deviceId); },
        async getDevices() { return core.getDevices(); },
        async setVideoMuted(muted) { req(); return core.setVideoMuted(muted); },
        async setAudioMuted(muted) { req(); return core.setAudioMuted(muted); },
        async setScreenshareMuted(muted) { req(); return core.setScreenshareMuted(muted); },
        setLocalCamera(videoEl) { req(); core.setLocalCameraElement(videoEl); },
        setLocalScreen(videoEl) { req(); core.setLocalScreenElement(videoEl); },
        setRemoteCamera(memberId, videoEl) { req(); core.setRemoteCameraElement(memberId, videoEl); },
        setRemoteScreen(memberId, videoEl) { req(); core.setRemoteScreenElement(memberId, videoEl); },
        clearLocalCamera() { req(); core.clearLocalCameraElement(); },
        clearLocalScreen() { req(); core.clearLocalScreenElement(); },
        clearRemoteCamera(memberId) { req(); core.clearRemoteCameraElement(memberId); },
        clearRemoteScreen(memberId) { req(); core.clearRemoteScreenElement(memberId); },
        registerTile(memberId, streamType, element) { req(); core.registerTile(memberId, streamType, element); },
        unregisterTile(memberId, streamType) { req(); core.unregisterTile(memberId, streamType); },
        async sendMessage(channelId, content, memberId = null) { req(); return core.sendMessage({ channelId, memberId, content }); },
        async getMessages(channelId, options = {}) { req(); return core.getMessages({ channelId, ...options }); },
        async deleteMessage(messageId) { req(); return core.deleteMessage(messageId); },
        async deleteTextHistory(channelId, options = {}) { req(); return core.deleteTextHistory(channelId, options); },
        async getTranscript(channelId) { req(); return core.getTranscript(channelId); },
        async getSessions(channelId, options = {}) { req(); return core.getSessions(channelId, options); },
        async getSummaries(channelId, options = {}) { req(); return core.getSummaries(channelId, options); },
        setOnMessage(callback) { req(); return core.setOnMessage(callback); },
        // Availability & Invites (Phase A)
        async setAvailable(opts = {}) { req(); return core.setAvailable(opts); },
        async getAvailableUsers(opts = {}) { req(); return core.getAvailableUsers(opts); },
        async getInvites(opts = {}) { req(); return core.getInvites(opts); },
        async sendInvite(opts) { req(); return core.sendInvite(opts); },
        async createRoomWithInvites(opts) { req(); return core.createRoomWithInvites(opts); },
        async acceptInvite(opts) { req(); return core.acceptInvite(opts); },
        async declineInvite(opts) { req(); return core.declineInvite(opts); },
        getMember(memberId) { req(); return core.getMember(memberId); },
        getMemberIds() { req(); return core.getMemberIds(); },
        async getMembers() { req(); return core.getCurrentChannelMembers(); },
        getMediaStates(memberId) { req(); return core.getMemberMediaStates(memberId); },
        getMemberState(memberId) { req(); return core.getMemberState(memberId); },
        getStream(memberId, streamType) { req(); return core.getMemberStream(memberId, streamType); },
        // Custom labeled upstreams + subscription + discovery
        async publishStream(label, opts) { req(); return core.publishStream(label, opts); },
        unpublishStream(label) { req(); core.unpublishStream(label); },
        subscribe(memberId, label, kinds) { req(); return core.subscribe(memberId, label, kinds); },
        unsubscribe(memberId, label) { req(); return core.unsubscribe(memberId, label); },
        setDefaultSubscription(map) { req(); core.setDefaultSubscription(map); },
        getMemberStreamLabels(memberId) { req(); return core.getMemberStreamLabels(memberId); },
        getConnectionQuality() { req(); return core.connectionQuality; },
        getMemberQuality(memberId) { req(); return core.getMemberQuality(memberId); },
        on(event, callback) {
            req();
            const prop = EVENT_MAP[event];
            if (!prop) throw new MakedoError(ErrorCodes.INVALID_PARAM, `Unknown event: "${event}". Valid events: ${Object.keys(EVENT_MAP).join(', ')}`);
            core[prop] = callback;
            return instance;
        },
        get isLoggedIn() { return core?.isLoggedIn ?? false; },
        get isLive() { return core?.isInChannel ?? false; },
        get user() { return core?.user ?? null; },
        get memberId() { return core?.localMemberId ?? null; },
        get roomCode() { return core?.currentRoomCode ?? null; },
        get channel() { return core?.selectedChannel ?? null; },
        get channels() { return core?.channels ?? []; },
        get mediaState() { return core?.localMediaState ?? { audio: false, video: false, audioMuted: false, videoMuted: false }; },
        get screenState() { return core?.screenshareState ?? { audio: false, video: false, videoMuted: false }; },
        get hasMedia() { return core?.hasAnyMedia ?? false; },
        get core() { return core; },
        version: '0.88',
        MakedoError,
        ErrorCodes,
    };

    return instance;
}

// Event name → VibeLiveCore callback property
const EVENT_MAP = {
    'login':              'onLogin',
    'loginError':         'onLoginError',
    'logout':             'onLogout',
    'channelsLoaded':     'onChannelsLoaded',
    'usersLoaded':        'onUsersLoaded',
    'channelSelected':    'onChannelSelected',
    'localJoined':        'onLocalMemberJoined',
    'localLeft':          'onLocalMemberLeft',
    'localLiveChange':    'onLocalLiveChange',
    'remoteJoined':       'onRemoteMemberJoined',
    'remoteLeft':         'onRemoteMemberLeft',
    'memberUpdate':       'onMemberUpdate',
    'remoteStreamStart':  'onRemoteMemberStreamStart',
    'remoteStreamEnd':    'onRemoteMemberStreamEnd',
    'remoteLiveChange':   'onRemoteMemberLiveChange',
    'remoteMediaChange':  'onRemoteMemberMediaChange',
    'localMediaChange':   'onLocalMemberMediaChange',
    'memberStateChange':  'onMemberStateChange',
    'kicked':             'onKickedExt',
    'kickWarning':        'onKickWarning',
    'livelinessCheck':    'onLivelinessCheck',
    'connectionQuality':  'onConnectionQuality',
    'memberQuality':      'onMemberQuality',
    'inviteReceived':     'onInviteReceived',
    'inviteResponse':     'onInviteResponse',
    'availabilityUpdate': 'onAvailabilityUpdate',
    'transcriptionStateChange': 'onTranscriptionStateChange',
    'sessionSummaryReady': 'onSessionSummaryReady',
    'error':              'onError',
    'warning':            'onWarning',
};

const VibeLive = {

    // ==================== Initialization ====================

    /**
     * Initialize VibeLive with your context credentials.
     * Must be called before any other method.
     * Creates the default singleton instance — for multi-room, use createInstance() instead.
     *
     * @param {Object} config
     * @param {string} config.projectId - Your app's project ID (contextId also accepted)
     * @param {string} [config.projectKey] - Project auth key for non-proxy authorization
     * @param {string} [config.proxy] - Proxy URL for production projects
     * @param {string} [config.serverUrl] - Server base URL (default: https://makedo.com)
     * @param {Object} [config.autoMatch] - Optional auto-match defaults. When the server-side
     *     project has `autoMatchEnabled=true`, any user who calls `setAvailable({open:true})`
     *     becomes a candidate. These fields are merged into every setAvailable call as
     *     defaults (per-call values still win):
     *       - role:       {string}  e.g. 'agent' | 'customer' | 'visitor'
     *       - attributes: {Object<string,string>}  free-form tags strategies may read
     *     Server config (`autoMatchStrategy`) controls which algorithm is used; the client
     *     does not pick it.
     * @param {Object<string,string[]>} [config.defaultSubscription] - Default downstream
     *     subscription map applied on join, e.g. { '': ['audio','video'] } (the default —
     *     receive everything). The '' key is the wildcard for any unlisted label. Narrow it
     *     (e.g. { '': ['audio'] }) to save bandwidth in large rooms, then subscribe() to
     *     specific video on demand. Overridable at runtime via setDefaultSubscription().
     * @param {boolean} [config.trackMembers=true] - When false, the SDK does NOT fetch per-member
     *     data (getOneMember) in response to presence events; it builds minimal member entries
     *     instead (displayName defaults to 'Unknown'). This eliminates the O(N²) getOneMember
     *     storm in large rooms (every member fetching every other member on join). Use it for
     *     clients that don't render the member roster — a mosaic compositor (which seats by
     *     memberId) or publish-only "leaf" members. Presence events (remoteJoined/remoteLeft/
     *     remoteStreamStart, memberUpdate) still fire; getMembers() (a single batched call) still
     *     works. Leave true (default) for normal apps that display member names/state.
     * @returns {Object} VibeLive (for chaining)
     */
    init(config = {}) {
        const BUILD = (typeof __BUILD_TIME__ !== 'undefined') ? __BUILD_TIME__ : 'dev';
        console.warn(`[VibeLive] SDK loaded — build: ${BUILD}  projectId=${config.projectId ?? config.contextId ?? '(none)'}`);
        MakedoConfig.configure(config.serverUrl);
        chat = new VibeLiveCore(config);
        return VibeLive;
    },

    /**
     * Create an independent VibeLive instance for multi-room scenarios.
     * Each instance has its own connection, state, events, and tile registry.
     *
     * Usage:
     *   const room1 = VibeLive.createInstance({ projectId: '...' });
     *   const room2 = VibeLive.createInstance({ projectId: '...' });
     *   room1.on('memberStateChange', ...);
     *   room2.on('memberStateChange', ...);
     *
     * @param {Object} config - Same config as init()
     * @returns {Object} A standalone VibeLive-compatible object
     */
    createInstance(config = {}) {
        return createInstance(config);
    },

    // ==================== Authentication ====================

    /**
     * Sign up as an anonymous guest.
     * Must be awaited before any other calls.
     * @param {string} name - Display name
     */
    async signup(name) {
        requireInit();
        return chat.signupAnonymous(name);
    },

    /**
     * Login with an existing account.
     * @param {string} email
     * @param {string} password
     * @param {string} [contextId]
     */
    async login(email, password, contextId) {
        requireInit();
        return chat.login(email, password, contextId);
    },

    /**
     * Resume the previous session from the auth cookie (set by any successful
     * login/signup; 24h max-age). Resolves to the user on success, null when no
     * valid session is stored. Never throws for a missing/expired token.
     * The cookie is origin-wide: the restored identity is whichever login or
     * anonymous signup last succeeded in this browser profile.
     */
    async restoreLogin() {
        requireInit();
        return chat.restoreLogin();
    },

    /** Logout and cleanup. */
    async logout() {
        requireInit();
        return chat.logout();
    },

    // ==================== Rooms ====================

    /**
     * Create a new room.
     * @param {string} title - Room title
     * @param {Object} [options] - Additional options (e.g. { allowsGuests: true }). Room config flags
     *   such as transcriptionActive are forwarded to createChannel; transcriptionActive turns recording
     *   on for this room (requires the project's transcriptionAllowed capability) and, when omitted,
     *   inherits the app-wide default from init({ features: { transcriptionActive: true } }).
     * @returns {Promise<Object>} { id, room_code, title }
     */
    async createRoom(title, options = {}) {
        requireInit();
        return chat.createChannel({ title, ...options });
    },

    /**
     * Enter a room by its shareable code → PRE-LIVE state.
     * Call startLive() after this to start streaming.
     * @param {string} roomCode
     * @param {string} [displayName] - Optional display name to use for this channel
     */
    async enterByRoomCode(roomCode, displayName = null) {
        requireInit();
        return chat.enterByRoomCode(roomCode, displayName);
    },

    /**
     * Exit the room completely. Releases camera/mic.
     */
    exitRoom() {
        requireInit();
        chat.exitChannel();
    },

    // ==================== Channel/User Management (login flow) ====================

    /** Load available channels (for logged-in users). */
    async loadChannels() {
        requireInit();
        return chat.loadChannels();
    },

    /** 
     * Select a channel from the loaded list → PRE-LIVE state.
     * @param {string} channelId
     * @param {string} [displayName] - Optional display name to use for this channel
     */
    async selectChannel(channelId, displayName = null) {
        requireInit();
        return chat.selectChannel(channelId, displayName);
    },

    /** Load available users (for logged-in users). */
    async loadUsers() {
        requireInit();
        return chat.loadUsers();
    },

    /** Start a quick-chat channel with a user → PRE-LIVE state. */
    async selectUser(userId) {
        requireInit();
        return chat.selectUser(userId);
    },

    /**
     * Get channel info by any identifier, or the current channel if none provided.
     *
     * Resolution order:
     *   1. No argument → returns the current channel (freshly fetched from server)
     *   2. channelId (e.g. VibeLive.channel?.id) → tries channel_id lookup first
     *   3. room code (e.g. VibeLive.roomCode) → falls back automatically if channelId lookup returns 404
     *
     * All three forms work: `getChannelInfo()`, `getChannelInfo(VibeLive.channel?.id)`,
     * `getChannelInfo(VibeLive.roomCode)`.
     *
     * @param {string} [identifier] - channelId, room code, or omit for current channel
     * @returns {Promise<Object|null>} Channel object, or null if no channel is active and no identifier given
     */
    async getChannelInfo(identifier) {
        requireInit();
        return chat.getChannelInfo(identifier);
    },

    /**
     * Update a room's built-in fields (owner-only): title, description, maxSize,
     * allowsGuests. Distinct from setRoomMetadata() — these are the platform-level
     * channel columns shown in room lists and console views.
     * @param {Object} options - { title?, description?, maxSize?, allowsGuests? }
     * @param {string} [channelId] - Defaults to the current channel
     * @returns {Promise<Object>} The updated channel view
     */
    async updateRoom(options, channelId) {
        requireInit();
        return chat.updateRoom(options, channelId);
    },

    /**
     * Set (or clear) the app-defined metadata blob on a room. Owner-only.
     * The blob is any JSON-serializable value (room profile, schedule, policies…) and
     * comes back, parsed, as `.metadata` on the channel object from getChannelInfo() —
     * readable without joining the room.
     * @param {Object|null} metadata - JSON-serializable value; null clears the metadata
     * @param {string} [channelId] - Defaults to the current channel; pass explicitly to
     *                               edit a room you have not entered
     * @returns {Promise<Object>} { success: true }
     */
    async setRoomMetadata(metadata, channelId) {
        requireInit();
        return chat.setRoomMetadata(metadata, channelId);
    },

    /**
     * Remove a member from the room. Owner-only (room creator or sys-admin).
     * Works for both LIVE and lobby (PRE-LIVE) members — the removed member's SDK
     * fires its `kicked` event and their registered tiles are cleaned up.
     * @param {string} memberId - The member to remove
     * @param {string} [message] - Optional message shown to the removed member
     * @returns {Promise<Object>} { success: true }
     */
    async kickMember(memberId, message) {
        requireInit();
        return chat.kickMember(memberId, message);
    },

    /** Go back to channel list (cleanup without full logout). */
    backToList() {
        requireInit();
        chat.backToChannels();
    },

    // ==================== Live State ====================

    /**
     * Start LIVE — connect WebRTC and start streaming.
     * Must call enterByRoomCode() or selectChannel() first.
     */
    async startLive() {
        requireInit();
        return chat.startLive();
    },

    /**
     * Stop LIVE — disconnect WebRTC, return to PRE-LIVE.
     * @param {boolean} [keepMedia=true] - Keep camera/mic running for preview
     */
    stopLive(keepMedia = true) {
        requireInit();
        chat.stopLive(keepMedia);
    },

    /**
     * Acknowledge a server liveliness check — resets the zombie-detection clock.
     * Call this from within your livelinessCheck handler once the user confirms
     * they are still present. Has no effect if no check is pending.
     */
    ackLiveliness() {
        requireInit();
        chat.ackLiveliness();
    },

    // ==================== Media Controls ====================

    /** Set video on/off (camera hardware). Idempotent. */
    async setVideo(on) {
        requireInit();
        return chat.setVideo(on);
    },

    /** Set audio on/off (microphone hardware). Idempotent. */
    async setAudio(on) {
        requireInit();
        return chat.setAudio(on);
    },

    /** Set screenshare on/off. Idempotent. */
    async setScreenshare(on) {
        requireInit();
        return chat.setScreenshare(on);
    },

    /** Switch to a specific camera device. Turns video on if needed. */
    async setCamera(deviceId) {
        requireInit();
        return chat.setCamera(deviceId);
    },

    /** Switch to a specific microphone device. Turns audio on if needed. */
    async setMicrophone(deviceId) {
        requireInit();
        return chat.setMicrophone(deviceId);
    },

    /** Enumerate available cameras, microphones, and speakers. */
    async getDevices() {
        return chat.getDevices();
    },

    /** Set video muted/hidden (keeps capturing). Idempotent. */
    async setVideoMuted(muted) {
        requireInit();
        return chat.setVideoMuted(muted);
    },

    /** Set audio muted (keeps capturing). Idempotent. */
    async setAudioMuted(muted) {
        requireInit();
        return chat.setAudioMuted(muted);
    },

    /** Set screenshare muted/hidden. Idempotent. */
    async setScreenshareMuted(muted) {
        requireInit();
        return chat.setScreenshareMuted(muted);
    },

    /** @deprecated Use setVideo(on). Toggle video on/off. */
    async toggleVideo() {
        requireInit();
        return chat.toggleVideo();
    },

    /** @deprecated Use setAudio(on). Toggle audio on/off. */
    async toggleAudio() {
        requireInit();
        return chat.toggleAudio();
    },

    /** @deprecated Use setScreenshare(on). Toggle screenshare on/off. */
    async toggleScreenshare() {
        requireInit();
        return chat.toggleScreenshare();
    },

    /** @deprecated Use setVideoMuted(muted). Toggle video hide/show. */
    async toggleMuteVideo() {
        requireInit();
        return chat.toggleMuteVideo();
    },

    /** @deprecated Use setAudioMuted(muted). Toggle audio mute/unmute. */
    async toggleMuteAudio() {
        requireInit();
        return chat.toggleMuteAudio();
    },

    /** @deprecated Use setScreenshareMuted(muted). Toggle screenshare hide/show. */
    async toggleMuteScreenshare() {
        requireInit();
        return chat.toggleMuteScreenshare();
    },

    // ==================== Element Registration ====================

    /** Register the local camera video element. */
    setLocalCamera(videoEl) {
        requireInit();
        chat.setLocalCameraElement(videoEl);
    },

    /** Register the local screenshare video element. */
    setLocalScreen(videoEl) {
        requireInit();
        chat.setLocalScreenElement(videoEl);
    },

    /** Register a remote member's camera video element. */
    setRemoteCamera(memberId, videoEl) {
        requireInit();
        chat.setRemoteCameraElement(memberId, videoEl);
    },

    /** Register a remote member's screenshare video element. */
    setRemoteScreen(memberId, videoEl) {
        requireInit();
        chat.setRemoteScreenElement(memberId, videoEl);
    },

    /** Unregister the local camera element. */
    clearLocalCamera() {
        requireInit();
        chat.clearLocalCameraElement();
    },

    /** Unregister the local screenshare element. */
    clearLocalScreen() {
        requireInit();
        chat.clearLocalScreenElement();
    },

    /** Unregister a remote member's camera element. */
    clearRemoteCamera(memberId) {
        requireInit();
        chat.clearRemoteCameraElement(memberId);
    },

    /** Unregister a remote member's screenshare element. */
    clearRemoteScreen(memberId) {
        requireInit();
        chat.clearRemoteScreenElement(memberId);
    },

    // ==================== Tile Registry ====================

    /**
     * Register a DOM element as a tile for a member's stream.
     * The SDK will auto-remove registered tiles from the DOM when:
     *   - The member exits the room
     *   - A screenshare stream ends
     *   - You call exitRoom() or get kicked
     *
     * Also auto-finds the <video> inside and registers it (no separate setRemoteCamera call needed).
     *
     * @param {string} memberId
     * @param {'camera'|'screenshare'} streamType
     * @param {HTMLElement} element - The tile DOM element (must contain a <video> child)
     */
    registerTile(memberId, streamType, element) {
        requireInit();
        chat.registerTile(memberId, streamType, element);
    },

    /**
     * Manually unregister and remove a tile. Rarely needed -- lifecycle cleanup is automatic.
     * @param {string} memberId
     * @param {'camera'|'screenshare'} streamType
     */
    unregisterTile(memberId, streamType) {
        requireInit();
        chat.unregisterTile(memberId, streamType);
    },

    // ==================== Messaging ====================

    /**
     * Send a text message to a channel or privately to a member.
     * @param {string} [channelId] - The channel ID (defaults to current channel if omitted or null)
     * @param {string} content - The message text
     * @param {string} [memberId] - Omit for channel-wide; provide for a private message
     * @returns {Promise<Object>} The created message object
     */
    async sendMessage(channelId, content, memberId = null) {
        requireInit();
        return chat.sendMessage({ channelId, memberId, content });
    },

    /**
     * Fetch recent messages for a channel or member conversation.
     * @param {string} [channelId] - The channel ID (defaults to current channel if omitted or null)
     * @param {Object} [options]
     * @param {string} [options.memberId] - Member ID for private messages
     * @param {number} [options.count=10] - Number of messages to return
     * @param {number} [options.page=0] - Page offset (0-based)
     * @returns {Promise<Array>} Array of message objects (reverse-chronological)
     */
    async getMessages(channelId, options = {}) {
        requireInit();
        return chat.getMessages({ channelId, ...options });
    },

    /**
     * Soft-delete a single message. Only the message author may call this.
     * @param {string} messageId - The message ID (`pid` from a message object)
     * @returns {Promise<Object>} { success: true }
     */
    async deleteMessage(messageId) {
        requireInit();
        return chat.deleteMessage(messageId);
    },

    /**
     * Soft-delete all message history for a channel.
     * Requires the caller to be the room owner or a system admin.
     * @param {string} [channelId] - The channel ID (defaults to current channel)
     * @param {Object} [options]
     * @param {boolean} [options.roomMessages=true]  Delete channel-wide messages
     * @param {boolean} [options.dmMessages=false]   Also delete member DMs in this room
     * @returns {Promise<Object>} { success: true }
     */
    async deleteTextHistory(channelId, options = {}) {
        requireInit();
        return chat.deleteTextHistory(channelId, options);
    },

    /**
     * Retrieve the transcript for a channel's recorded session. Returns on-demand
     * (no polling). For per-session AI summaries, use getSessions() instead.
     * @param {string} [channelId] - The channel ID (defaults to the current channel)
     * @returns {Promise<Object>} { channelId, status, text, segments }
     */
    async getTranscript(channelId) {
        requireInit();
        return chat.getTranscript(channelId);
    },

    /**
     * Retrieve the list of session summaries for a channel. Each session is one
     * member's continuous time in the room; `summary` is the AI-generated headline.
     * Visibility is requestor-aware on the server: hosts/admins see all sessions,
     * members see only sessions they participated in. Pair with the
     * 'sessionSummaryReady' event to know when a new summary is ready.
     * @param {string} [channelId] - The channel ID (defaults to the current channel)
     * @param {Object} [options] - { depth, count }
     * @returns {Promise<Array>} session objects: { sessionId, title, summary, tags, ... }
     */
    async getSessions(channelId, options = {}) {
        requireInit();
        return chat.getSessions(channelId, options);
    },

    /** Alias for {@link getSessions}. */
    async getSummaries(channelId, options = {}) {
        requireInit();
        return chat.getSummaries(channelId, options);
    },

    /**
     * Register a handler for real-time incoming text messages delivered over
     * the WebSocket. Fires whenever a message is broadcast to the logged-in
     * user (channel/interval messages or private DMs sent BY others).
     * The author does NOT receive an echo of their own send.
     * @param {(message: Object, wsIncoming: Object) => void} callback
     *        message: { pid, owner_id, target_type, target_id, content, content_type, cts, ... }
     */
    setOnMessage(callback) {
        requireInit();
        return chat.setOnMessage(callback);
    },

    // ==================== Member Data ====================

    /**
     * Get member info by ID.
     * @returns {{ displayName, displayStatus, hasCamera, hasScreenshare } | null}
     *   displayStatus: 'LIVE' (streaming), 'PRE-LIVE' (preparing), 'EXITED' (left the room)
     */
    getMember(memberId) {
        requireInit();
        return chat.getMember(memberId);
    },

    /** Get all member IDs in the current channel. */
    getMemberIds() {
        requireInit();
        return chat.getMemberIds();
    },

    /** Fetch all current channel members from server. */
    async getMembers() {
        requireInit();
        return chat.getCurrentChannelMembers();
    },

    /**
     * Get media states for a member.
     * @returns {{ audioDetail, videoDetail, screenAudioDetail, screenVideoDetail }}
     */
    getMediaStates(memberId) {
        requireInit();
        return chat.getMemberMediaStates(memberId);
    },

    /**
     * Get unified state for any member (local or remote). Same shape always.
     * @param {string} memberId
     * @returns {{ live: boolean, video: string, audio: string, screenVideo: string }}
     *   video/audio/screenVideo: 'ON' | 'MUTED' | 'OFF'
     */
    getMemberState(memberId) {
        requireInit();
        return chat.getMemberState(memberId);
    },

    /** Get the raw MediaStream for a member. */
    getStream(memberId, streamType) {
        requireInit();
        return chat.getMemberStream(memberId, streamType);
    },

    // ==================== Custom labeled upstreams + subscription ====================

    /**
     * Publish an arbitrary-label upstream alongside camera/screenshare — e.g. a low-bandwidth
     * thumbnail, a processed canvas stream, or a second camera. Reserved labels
     * ('camera'/'screenshare'/'microphone'/'movie') are rejected. The label must be unique.
     * Remote peers discover it via the 'remoteStreamStart' event and read it with getStream(id, label).
     * Give exactly one source: an app-supplied `stream`, capture `constraints`, or `from` —
     * the label of an existing stream to derive this one from by downscaling.
     *
     *   // a 64x64 mosaic tile rendered from the live camera, at 5fps and 30kbps
     *   await VibeLive.publishStream('thumbnail', {
     *       from: 'camera', width: 64, height: 64, fps: 5,
     *       encodings: [{ maxBitrate: 30000 }],
     *   });
     *
     * The camera is read once and fanned out, so the tile costs a downscale blit rather than a
     * second capture, and 'camera' keeps publishing its own native track untouched — adding or
     * removing a derived stream never interrupts it. A derived stream survives the camera being
     * switched off (it publishes black and resumes when the camera returns) and ends when the
     * source stream is stopped outright.
     *
     * @param {string} label
     * @param {{stream?: MediaStream, constraints?: MediaStreamConstraints, from?: string, width?: number, height?: number, fps?: number, fit?: 'cover'|'contain', encodings?: RTCRtpEncodingParameters[], contentHint?: 'motion'|'detail'|'text'|''}} opts
     *   from requires width and height; fps defaults to 15, fit to 'cover' (centre-crop, which
     *   is what uniform grid tiles want — 'contain' letterboxes instead).
     *   contentHint defaults to 'motion' (favors frame rate); pass 'detail' for text/screen-like content.
     * @returns {Promise<MediaStream>}
     */
    async publishStream(label, opts) {
        requireInit();
        return chat.publishStream(label, opts);
    },

    /** Stop and remove a custom labeled upstream. */
    unpublishStream(label) {
        requireInit();
        chat.unpublishStream(label);
    },

    /**
     * Control which tracks the SFU sends you for one remote stream.
     * @param {string} memberId
     * @param {string} label - 'camera' | 'screenshare' | custom label
     * @param {string[]} kinds - ['audio','video'] | ['video-low'] | ['audio'] | []
     * @returns {boolean} false if that stream is not currently present locally
     */
    subscribe(memberId, label, kinds) {
        requireInit();
        return chat.subscribe(memberId, label, kinds);
    },

    /** Stop receiving a specific remote stream (keeps it discoverable). */
    unsubscribe(memberId, label) {
        requireInit();
        return chat.unsubscribe(memberId, label);
    },

    /**
     * Set the default subscription map applied to all streams on join.
     * '' is the wildcard for unlisted labels. Default: { '': ['audio','video'] }.
     * @param {Object<string,string[]>} map
     */
    setDefaultSubscription(map) {
        requireInit();
        chat.setDefaultSubscription(map);
    },

    /** Discovery: labels a member is currently publishing that we have received. */
    getMemberStreamLabels(memberId) {
        requireInit();
        return chat.getMemberStreamLabels(memberId);
    },

    /** Current overall network-quality snapshot (only when feature enabled). */
    getConnectionQuality() {
        requireInit();
        return chat.connectionQuality;
    },

    /** Per-member network-quality snapshot (only when feature enabled). */
    getMemberQuality(memberId) {
        requireInit();
        return chat.getMemberQuality(memberId);
    },

    // ==================== Availability & Invites (Phase A) ====================

    /** Mark self available within the current project so invites can be delivered.
     *  @param {Object} opts
     *  @param {boolean} [opts.open=true] - false to set unavailable
     *  @param {number}  [opts.ttlSeconds=3600] - availability auto-expires after this
     *  @param {string}  [opts.useProfile=''] - opaque "about me" string
     *  @param {string}  [opts.seeking=''] - opaque "what I want" string
     */
    async setAvailable(opts = {}) {
        requireInit();
        return chat.setAvailable(opts);
    },

    /** List users currently available in the caller's project. */
    async getAvailableUsers(opts = {}) {
        requireInit();
        return chat.getAvailableUsers(opts);
    },

    /** Get pending invites for self. */
    async getInvites(opts = {}) {
        requireInit();
        return chat.getInvites(opts);
    },

    /** Send an invite for an existing channel to a user. */
    async sendInvite(opts) {
        requireInit();
        return chat.sendInvite(opts);
    },

    /** Create a new room and pre-invite a list of users. */
    async createRoomWithInvites(opts) {
        requireInit();
        return chat.createRoomWithInvites(opts);
    },

    /** Accept an invite. Auto-enters the room unless enterImmediately:false. */
    async acceptInvite(opts) {
        requireInit();
        return chat.acceptInvite(opts);
    },

    /** Decline an invite. */
    async declineInvite(opts) {
        requireInit();
        return chat.declineInvite(opts);
    },

    // ==================== Events ====================

    /**
     * Register an event handler.
     * 
     * Events:
     *   login, loginError, logout, channelsLoaded, usersLoaded,
     *   channelSelected, localJoined, localLeft,
     *   remoteJoined, remoteLeft, memberUpdate,
     *   remoteStreamStart, remoteStreamEnd, remoteLiveChange, remoteMediaChange,
     *   localMediaChange, localLiveChange, memberStateChange, error
     * 
     * @param {string} event - Event name
     * @param {Function} callback - Handler function
     * @returns {Object} VibeLive (for chaining)
     */
    on(event, callback) {
        requireInit();
        const prop = EVENT_MAP[event];
        if (!prop) {
            throw new Error(
                `Unknown event: "${event}". Valid events: ${Object.keys(EVENT_MAP).join(', ')}`
            );
        }
        chat[prop] = callback;
        return VibeLive;
    },

    // ==================== Getters ====================

    /** Is the user authenticated? */
    get isLoggedIn() { return chat?.isLoggedIn ?? false; },

    /** Is WebRTC connected (LIVE state)? */
    get isLive() { return chat?.isInChannel ?? false; },

    /** Current user object. */
    get user() { return chat?.user ?? null; },

    /** Your member ID in the current channel. */
    get memberId() { return chat?.localMemberId ?? null; },

    /** Shareable room code for the current channel. */
    get roomCode() { return chat?.currentRoomCode ?? null; },

    /** Current channel object. */
    get channel() { return chat?.selectedChannel ?? null; },

    /** Loaded channels list (after loadChannels()). */
    get channels() { return chat?.channels ?? []; },

    /** Local camera/mic state: { audio, video, audioMuted, videoMuted }. */
    get mediaState() {
        return chat?.localMediaState ?? { audio: false, video: false, audioMuted: false, videoMuted: false };
    },

    /** Local screenshare state: { video, videoMuted }. */
    get screenState() {
        return chat?.screenshareState ?? { audio: false, video: false, videoMuted: false };
    },

    /** True if any local media (camera, mic, or screenshare) is active. */
    get hasMedia() { return chat?.hasAnyMedia ?? false; },

    // ==================== Escape Hatch ====================

    /** Access the underlying VibeLiveCore instance for advanced use. */
    get core() { return chat; },

    // ==================== Error Types ====================

    /** SDK version string. */
    version: '0.88',

    /** MakedoError class for instanceof checks. */
    MakedoError,

    /** Error code constants for programmatic handling. */
    ErrorCodes,
};

// Attach to window for onclick handlers
window.VibeLive = VibeLive;

export default VibeLive;
